import { useEffect, useState, useRef } from "react";
import { FMDPWebSocket } from "../../containers/Shared/WebSocket";

/**
 * Custom React hook for managing WebSocket connections for real-time report updates
 *
 * @param {number|string} userId - The current user's ID for WebSocket connection
 * @param {function} onReportUpdate - Callback function invoked when a report update is received
 * @returns {object} - Object containing connectionStatus ('connected' | 'disconnected' | 'error')
 */
export const useReportWebSocket = (userId, onReportUpdate) => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const callbackRef = useRef(onReportUpdate);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onReportUpdate;
  }, [onReportUpdate]);

  useEffect(() => {
    // Don't establish connection if userId is not provided
    if (!userId) {
      setConnectionStatus('disconnected');
      return;
    }

    try {
      // Initialize WebSocket connection
      const ws = new FMDPWebSocket(userId);
      wsRef.current = ws;

      // Subscribe to ResourceUpdateNotificationChannel
      ws.subscribe("ResourceUpdateNotificationChannel", (data) => {
        try {
          console.log('reports', data.message)
          // Filter for report-related notifications
          if (data.message && data.message.resource_type === "reports") {
            // Invoke callback with report data
            if (callbackRef.current) {
              callbackRef.current(data.message?.data || {});
            }
          }
        } catch (error) {
          console.error("Error processing report notification:", error, data);
        }
      });

      // Set connection status to connected
      setConnectionStatus('connected');
    } catch (error) {
      console.error("WebSocket connection failed:", error);
      setConnectionStatus('error');
    }

    // Cleanup function - close WebSocket connection on unmount
    return () => {
      if (wsRef.current && wsRef.current.ws) {
        try {
          wsRef.current.ws.close();
        } catch (error) {
          console.error("Error closing WebSocket connection:", error);
        }
      }
      wsRef.current = null;
      setConnectionStatus('disconnected');
    };
  }, [userId]);

  return { connectionStatus };
};
